import React from "react";
import { every, groupBy } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { Button, Dialog, DialogActions, FormControl } from "@mui/material/";
import makeStyles from "@mui/styles/makeStyles";

import actions from "../../../Store/actions";
import { strings } from "../../../common/i18n";
import { enums, helpers } from "../../../common/lib";
import MatReactTable from "../MaterialReactTable";
import SelectAll from "../SelectAll";
import styles from "./styles";
import { logToSentry } from "../../../config/Sentry";

const canAddAssignment = (auditAssignments: any) =>
    every(
        auditAssignments,
        (item) =>
            item == null ||
            item.status === enums.status.auditAssignment.recalled ||
            item.status === enums.status.auditAssignment.submitted
    );

type Props = {
    onClose: Function,
    onSaveComplete: Function,
    open: boolean,
    type: String
};

/**
 * Edit Audit Assignment Add Dialog Component
 */
export default ({ onClose, open, onSaveComplete, type }: Props) => {
    const {
        accounts,
        auditAssets,
        auditAssignments,
        auditLocations,
        auth,
        locations,
        userLocations
    } = useSelector((state) => state);
    const [assignments, setAssignments] = React.useState({});
    const classes = makeStyles(styles)();
    const dispatch = useDispatch();
    const [loading, setLoading] = React.useState(false);

    const handleAssignmentsChange =
        (auditLocationId: number) => (ids: Array<number>) => {
            const next = { ...assignments };
            next[auditLocationId] = ids;
            setAssignments(next);
        };

    const columns = [
        {
            accessorKey: "location",
            header: strings("location")
        },
        {
            accessorKey: "assigned",
            header: strings("assigned"),
            sortingFn: "basic",
            Cell: ({ row }) => (
                <>
                    <div>{`${row.original.assigned}/${row.original.mandatory}`}</div>
                    {row.original.reissued > 0 && (
                        <div>{`${strings("reissued")}: ${row.original.reissued}`}</div>
                    )}
                </>
            )
        },
        {
            accessorKey: "auditors",
            header: strings("auditors"),
            enableSorting: false,
            Cell: ({ row }) => (
                <FormControl className={classes.selectContainer}>
                    <SelectAll
                        disabled={
                            row.original.assigned === row.original.mandatory &&
                            row.original.reissued === 0
                        }
                        items={row.original.auditors}
                        onChange={handleAssignmentsChange(row.original.auditLocationId)}
                        value={assignments[row.original.auditLocationId] || []}
                    />
                </FormControl>
            )
        }
    ];

    const data = React.useMemo(() => {
        const byAuditLocation = groupBy(auditAssets.data, "auditLocation");
        return auditLocations.data.map((item) => {
            const auditors = [];
            userLocations.data.forEach(({ group, location, user }) => {
                if (
                    group === enums.auth.group.auditor &&
                    location === item.location
                ) {
                    const forAuditorAuditLocation =
                        auditAssignments.data.filter(
                            (i) =>
                                i.auditor === user &&
                                i.auditLocation === item.id
                        );
                    const auditor = helpers.getListItemForId(accounts, user);
                    if (
                        auditor &&
                        Object.keys(auditor).length &&
                        canAddAssignment(forAuditorAuditLocation)
                    ) {
                        const { email, firstName, id, surname } = auditor;
                        auditors.push({
                            id,
                            name: `${email} \u2014 ${firstName} ${surname}`
                        });
                    }
                }
            });
            const location = helpers.getListItemForId(locations, item.location);
            const forLocation = byAuditLocation[item.id] || [];
            return {
                assigned: forLocation.filter((item) => item.isAssigned).length,
                auditLocationId: item.id,
                auditors,
                location: location.name,
                mandatory: forLocation.filter((item) => item.isMandatory)
                    .length,
                reissued: forLocation.filter(
                    (item) =>
                        item.reissuedStatus === enums.status.reissued.reissued
                ).length
            };
        });
    }, [
        accounts,
        auditAssets,
        auditAssignments,
        auditLocations,
        locations,
        userLocations
    ]);

    const onDialogClose = () => !loading && onClose && onClose();
    const onDialogEnter = () => setAssignments({});

    const onSave = async () => {
        setLoading(true);
        const is_reissued = type === "Reissuing" ? Boolean(1) : Boolean(0);
        const next = Object.entries(assignments).reduce(
            (aggregate, [key, value]) => [
                ...aggregate,
                ...value.map((auditor) => ({
                    auditLocation: Number(key),
                    is_reissued,
                    auditor,
                    createdBy: auth.account.id,
                    status: 1
                }))
            ],
            []
        );

        if (next.length)
            try {
                await dispatch(
                    actions.auditAssignments.create.postRequest(next)
                );
                dispatch(
                    actions.toast.create.success(
                        strings("messages.assetReissudToAuditors")
                    )
                );
            } catch (error) {
                const errStr = error.toString();
                console.error(errStr);
                logToSentry("On reissuing assets:", { error });
                dispatch(
                    actions.toast.create.error(
                        strings("errors.assetReissudToAuditors")
                    )
                );
            }
        setLoading(false);
        onDialogClose();
        onSaveComplete && onSaveComplete();
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={onDialogClose}
                TransitionProps={{
                    onEnter: onDialogEnter
                }}
            >
                <MatReactTable
                    persistKey="auditAssignmentsAdd"
                    columns={columns}
                    data={data}
                    loading={loading}
                    enablePagination={false}
                />
                <DialogActions>
                    <Button disabled={loading} onClick={onDialogClose}>
                        {strings("cancel")}
                    </Button>
                    <Button disabled={loading} onClick={onSave}>
                        {strings("save")}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};
